import { useState, useCallback, useRef } from 'react';
import { Upload, FileText, X, Loader2, AlertCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface DocumentImporterProps {
  onFileProcessed: (file: File, text: string) => void;
  onCancel: () => void;
  isProcessing?: boolean;
}

export function DocumentImporter({
  onFileProcessed,
  onCancel,
  isProcessing = false,
}: DocumentImporterProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [extracting, setExtracting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const allowedTypes = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
  ];

  const validateFile = (file: File): boolean => {
    setError(null);
    
    // Verificar tipo
    const isValidType = allowedTypes.includes(file.type) || 
      file.name.toLowerCase().endsWith('.pdf') ||
      file.name.toLowerCase().endsWith('.docx') ||
      file.name.toLowerCase().endsWith('.doc');
    
    if (!isValidType) {
      setError('Formato no soportado. Solo se permiten archivos PDF y DOCX.');
      return false;
    }
    
    // Verificar tamaño (20MB max)
    if (file.size > 20 * 1024 * 1024) {
      setError('El archivo es demasiado grande. Máximo 20MB.');
      return false;
    }
    
    return true;
  };

  const processFile = useCallback(async (file: File) => {
    if (!validateFile(file)) return;
    
    setSelectedFile(file);
    setExtracting(true);
    setError(null);
    
    try {
      // Importar dinámicamente para evitar cargar el servicio innecesariamente
      const { extractTextFromFile } = await import('@/services/textExtractionService');
      const result = await extractTextFromFile(file);
      
      if (!result.success) {
        setError(result.error || 'Error al extraer texto del documento');
        setExtracting(false);
        return;
      }
      
      onFileProcessed(file, result.text);
    } catch (err) {
      console.error('Error processing file:', err);
      setError('Error al procesar el documento. Intente de nuevo.');
    } finally {
      setExtracting(false);
    }
  }, [onFileProcessed]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      processFile(files[0]);
    }
  }, [processFile]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6">
      {/* Header con botón volver */}
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver
        </Button>
      </div>

      <div className="text-center">
        <h3 className="text-lg font-semibold mb-2">Importar documento</h3>
        <p className="text-sm text-muted-foreground">
          Sube un documento PDF o DOCX para extraer automáticamente los datos del expediente
        </p>
      </div>

      {/* Zona de drop */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !extracting && !isProcessing && fileInputRef.current?.click()}
        className={cn(
          'relative border-2 border-dashed rounded-xl p-8 transition-all cursor-pointer',
          'flex flex-col items-center justify-center min-h-[200px]',
          isDragging && 'border-primary bg-primary/5',
          !isDragging && 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50',
          (extracting || isProcessing) && 'cursor-wait opacity-70',
          error && 'border-destructive/50'
        )}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
          onChange={handleFileSelect}
          className="hidden"
          disabled={extracting || isProcessing}
        />

        {extracting || isProcessing ? (
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-12 w-12 text-primary animate-spin" />
            <p className="text-sm font-medium">
              {extracting ? 'Extrayendo texto del documento...' : 'Procesando datos...'}
            </p>
          </div>
        ) : selectedFile && !error ? (
          <div className="flex flex-col items-center gap-3">
            <div className="flex items-center gap-3 bg-muted rounded-lg px-4 py-3">
              <FileText className="h-8 w-8 text-primary" />
              <div className="text-left">
                <p className="font-medium text-sm truncate max-w-[200px]">{selectedFile.name}</p>
                <p className="text-xs text-muted-foreground">{formatFileSize(selectedFile.size)}</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearFile();
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : (
          <>
            <Upload className={cn(
              'h-12 w-12 mb-4',
              isDragging ? 'text-primary' : 'text-muted-foreground/50'
            )} />
            <p className="text-sm font-medium mb-1">
              Arrastra aquí tu documento
            </p>
            <p className="text-xs text-muted-foreground">
              o haz clic para seleccionar
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Formatos: PDF, DOCX • Máx: 20MB
            </p>
          </>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/30 rounded-lg">
          <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-destructive">Error</p>
            <p className="text-sm text-destructive/80">{error}</p>
          </div>
        </div>
      )}

      {/* Tipos de documentos soportados */}
      <div className="bg-muted/50 rounded-lg p-4">
        <p className="text-sm font-medium mb-2">Documentos soportados:</p>
        <ul className="text-sm text-muted-foreground space-y-1">
          <li className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Decretos y Resoluciones
          </li>
          <li className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Notificaciones de otras administraciones
          </li>
          <li className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Solicitudes e Instancias
          </li>
        </ul>
      </div>
    </div>
  );
}
